<script setup>
// Rajd przygodowy /ar/{alias} — makiety „Wyniki – makiety”: AR1 (komputer), AR2/AR3 (telefon), AR5 (bez stref).
// Nagłówek i stopka jak na /{alias} (EventHeader, PartnersFooter) — bez zakładek Wyniki/Międzyczasy/Statystyki.
// Kolumna na etap z ikoną i kolorem dyscypliny, kwadraty PK w 3 stanach, czas i miejsce na etapie, „teraz”
// u drużyn w trasie, kara wliczona w czas, rozwijana drużyna. Logika: utils/ar-results.js (raz na pobranie).
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, toRaw, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useResultsStore } from '../stores/results-fetcher.js'
import EventHeader from './results/EventHeader.vue'
import PartnersFooter from './results/PartnersFooter.vue'
import RunningTime from './results/RunningTime.vue'
import StageIcon from './ar/StageIcon.vue'
import { AR_ORDER, DISCIPLINE_LABELS, TASK_TYPE, buildArRows, buildArStages, checkpointLabel, filterArRows, markerName, stageDisciplines } from '../utils/ar-results.js'
import { categoryOptions } from '../utils/results-list.js'
import { betaEventUrl, eventPhase } from '../utils/event-info.js'
import { formatClock, parseApiTime } from '../utils/polish-time.js'

const REFRESH_MS = 30000
const CHANGED_MS = 30000
const PHONE_QUERY = '(max-width: 767.98px)'

const resultsStore = useResultsStore()
const route = useRoute()
const alias = String(route.params.eventAlias || '')

const loaded = ref(false)
const lastUpdate = ref(null)
const phase = ref(null)

// Magazyn trzyma poprzednie wydarzenie, dopóki nie przyjdzie nowe — bierzemy tylko to z adresu.
const eventPayload = computed(() => {
  const payload = resultsStore.event
  const ev = payload?.event
  return ev && (!ev.alias || ev.alias === alias) ? payload : null
})
const event = computed(() => eventPayload.value?.event || null)
const sponsors = computed(() => eventPayload.value?.sponsors || [])

// ---------- etapy i wiersze (raz na pobranie) ----------
const plan = shallowRef({ stages: [], hasZones: false, totalPoints: 0 })
const rows = shallowRef([])
const hasPoints = ref(false)
const hasGender = ref(false)
const categories = shallowRef([])
const changedIds = shallowRef(new Set())
let previousSignatures = null
let changedTimer = null

// Etapy do szablonu: kolor dyscypliny, kwadraty PK (numer, nazwa, zadanie specjalne = gwiazdka)
const stageViews = computed(() => plan.value.stages.map(stage => ({
  index: stage.index,
  discipline: stage.discipline,
  title: `Etap ${stage.index + 1}`,
  label: stage.label,
  color: stage.discipline ? `var(--sledz-stage-${stage.discipline})` : 'var(--sledz-stage-other)',
  endName: stage.end ? markerName(stage.end) : '',
  endIsZone: stage.endIsZone,
  points: stage.points.map(p => ({
    id: p.id,
    name: markerName(p) || `PK ${p.sort}`,
    label: checkpointLabel(p),
    task: Number(p.type) === TASK_TYPE
  }))
})))
const multiStage = computed(() => plan.value.hasZones && plan.value.stages.length > 1)
const hasTask = computed(() => stageViews.value.some(s => s.points.some(p => p.task)))
const legendDisciplines = computed(() => (multiStage.value ? stageDisciplines(plan.value.stages) : []))

function signature(row) {
  return `${row.group}|${row.timeText}|${row.place}|${row.punched}|${row.penaltyMin}|${row.points}`
}

function markChanged(built) {
  const next = new Map(built.map(row => [row.id, signature(row)]))
  if (previousSignatures) {
    const changed = new Set()
    next.forEach((sig, id) => {
      if (previousSignatures.has(id) && previousSignatures.get(id) !== sig) changed.add(id)
    })
    if (changed.size) {
      changedIds.value = changed
      clearTimeout(changedTimer)
      changedTimer = setTimeout(() => { changedIds.value = new Set() }, CHANGED_MS)
    }
  }
  previousSignatures = next
}

function rebuild() {
  const now = Date.now()
  const eventMarkers = toRaw(eventPayload.value?.markers) || []
  // magazyn wystawia markery /splits tylko jako mapę id → marker
  plan.value = buildArStages(Object.values(toRaw(resultsStore.markersMap) || {}), eventMarkers)
  categories.value = toRaw(resultsStore.categories) || []
  hasGender.value = resultsStore.hasGender()
  const built = buildArRows(toRaw(resultsStore.results) || [], plan.value, {
    categories: categories.value,
    eventStartMs: parseApiTime(event.value?.date_start),
    eventEndMs: parseApiTime(event.value?.date_end),
    nowMs: now
  })
  hasPoints.value = built.hasPoints
  markChanged(built.rows)
  rows.value = built.rows
  phase.value = eventPhase(event.value, now)
  lastUpdate.value = now
}

// ---------- filtry ----------
const query = ref('')
const gender = ref('all')
const category = ref('all')
const showDns = ref(false)
const sheetOpen = ref(false)
const expandedId = ref(null)

const catOptions = computed(() => categoryOptions(rows.value, categories.value))
const filtered = computed(() => filterArRows(rows.value, { query: query.value, gender: gender.value, category: category.value }))
const filtersActive = computed(() => gender.value !== 'all' || category.value !== 'all' || query.value.trim() !== '')
const activeFilterCount = computed(() => (gender.value !== 'all' ? 1 : 0) + (category.value !== 'all' ? 1 : 0))
const dnsVisible = computed(() => showDns.value || query.value.trim() !== '')
const categoryLabel = computed(() => catOptions.value.find(c => c.id === category.value)?.name || '')

function plural(n, one, few, many) {
  if (n === 1) return one
  const last = n % 10
  const lastTwo = n % 100
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? few : many
}

function clearFilters() {
  gender.value = 'all'
  category.value = 'all'
}

const orderNote = computed(() => {
  if (hasPoints.value) return 'Kolejność: punkty, potem czas z karami.'
  const base = AR_ORDER === 'checkpoints' ? 'Kolejność: najpierw liczba zaliczonych PK, potem czas z karami.' : 'Kolejność: czas z karami.'
  return multiStage.value ? `${base} Czas etapu: między podbiciami stref zmian (z pobytem w strefie).` : base
})
const orderNoteShort = computed(() => (hasPoints.value ? 'Kolejność: punkty, potem czas z karami' : AR_ORDER === 'checkpoints' ? 'Kolejność: liczba PK, potem czas z karami' : 'Kolejność: czas z karami'))

// ---------- lista z grupami ----------
const groups = computed(() => {
  const g = { finished: [], course: [], dnf: [], dns: [] }
  filtered.value.forEach(row => g[row.group].push(row))
  return g
})
const courseLabel = computed(() => (phase.value === 'before' ? 'Lista startowa' : phase.value === 'ended' ? 'Bez mety' : 'W trasie'))
const items = computed(() => {
  const g = groups.value
  const out = g.finished.map(row => ({ key: row.id, row }))
  if (g.course.length) {
    out.push({ key: 'h-course', section: `${courseLabel.value} · ${g.course.length}` })
    g.course.forEach(row => out.push({ key: row.id, row }))
  }
  if (g.dnf.length) {
    out.push({ key: 'h-dnf', section: `Nie ukończyli · ${g.dnf.length}` })
    g.dnf.forEach(row => out.push({ key: row.id, row }))
  }
  if (g.dns.length && dnsVisible.value) {
    out.push({ key: 'h-dns', section: `Nie wystartowali · ${g.dns.length}` })
    g.dns.forEach(row => out.push({ key: row.id, row }))
  }
  return out
})

const columnCount = computed(() => 5 + (catOptions.value.length ? 1 : 0) + (hasPoints.value ? 1 : 0) + (multiStage.value ? stageViews.value.length : 1))

function toggle(row) {
  expandedId.value = expandedId.value === row.id ? null : row.id
}

// ---------- teksty komórek ----------
function pkTitle(stage, point, row, i) {
  const state = row.cells[stage.index].states[i]
  const ms = row.cells[stage.index].points[i].ms
  if (state === 'done') return `${point.name} – zaliczony${ms != null ? ` ${formatClock(ms).slice(0, 5)}` : ''}`
  return `${point.name} – ${state === 'skipped' ? 'pominięty' : 'jeszcze przed drużyną'}`
}

// ---------- szerokość kolumn etapów ----------
// Kwadrat PK = 18 px + 3 px odstępu. Etap ma w rzędzie najwyżej `cap` kwadratów, dłuższy się zawija.
// Przy 4 etapach mieści się do 10 w rzędzie (makieta AR1); przy wielu etapach (np. 7) limit spada, a od
// 6 w rzędzie tabela jest „gęsta” (kwadraty 16 px, węższa kolumna drużyny, mniejsze nagłówki), żeby
// zmieściła się w karcie bez przewijania w bok. Dopiero gdy nawet 3 gęste w rzędzie się nie mieszczą
// (wąskie okno), tabela przewija się w bok z przyklejonymi kolumnami Miejsce i Drużyna
// (zgłoszenie 04.11.2026: rajd z 7 etapami — dwa pionowe paski i jeden poziomy).
const PK_STEP = 21
const PK_STEP_DENSE = 18
// układy od najszerszego; bez stref (AR5) jedna kolumna PK — do 20 w rzędzie
const STAGE_LAYOUTS = [
  ...[10, 9, 8, 7].map(cap => ({ cap, step: PK_STEP, dense: false })),
  ...[6, 5, 4, 3].map(cap => ({ cap, step: PK_STEP_DENSE, dense: true }))
]
const SINGLE_LAYOUTS = [20, 16, 12, 10, 8, 6].map(cap => ({ cap, step: PK_STEP, dense: false }))
const layouts = computed(() => (multiStage.value ? STAGE_LAYOUTS : SINGLE_LAYOUTS))
// Wybór po narysowaniu: od najszerszego układu, a gdy tabela wystaje z karty — następny węższy, aż się
// zmieści albo skończą się układy. Pomiar i zmiana dzieją się przed odmalowaniem (nextTick po renderze),
// więc nie widać przeskoków. Od nowa przy innej szerokości karty i innych etapach.
const layoutIndex = ref(0)
const cardRef = ref(null)
const scrollRef = ref(null)
const cardWidth = ref(0)
let cardObserver = null
watch(cardRef, el => {
  cardObserver?.disconnect()
  cardObserver = null
  if (!el || typeof ResizeObserver === 'undefined') return
  cardObserver = new ResizeObserver(entries => {
    const w = Math.round(entries[0]?.contentRect?.width || 0)
    if (w > 0 && w !== cardWidth.value) cardWidth.value = w
  })
  cardObserver.observe(el)
})
const stageLayout = computed(() => layouts.value[Math.min(layoutIndex.value, layouts.value.length - 1)])
const denseTable = computed(() => stageLayout.value.dense)
const pkRowWidth = (n, step) => n * step - (step === PK_STEP ? 3 : 2) // bez odstępu za ostatnim
function checkTableFits() {
  const el = scrollRef.value
  if (!el) return
  if (el.scrollWidth > el.clientWidth + 1 && layoutIndex.value < layouts.value.length - 1) layoutIndex.value += 1
}
watch([cardWidth, multiStage, () => stageViews.value.length, () => plan.value.totalPoints], () => { layoutIndex.value = 0 })
// cień przy przyklejonej kolumnie Drużyna, gdy tabela jest przewinięta w bok
const scrolledX = ref(false)
function onTableScroll(e) {
  scrolledX.value = e.target.scrollLeft > 0
}
// cardWidth też tutaj: przy zwężeniu okna z indeksem 0 reset nie zmienia układu, a tabela mogła przestać się mieścić
watch([stageLayout, rows, expandedId, scrollRef, cardWidth], () => nextTick(checkTableFits), { flush: 'post' })
function pksStyle(stage) {
  const { cap, step } = stageLayout.value
  const n = Math.min(stage.points.length, cap)
  if (!n) return undefined
  const width = `${pkRowWidth(n, step)}px`
  return { minWidth: width, maxWidth: width }
}
// rozwinięcie drużyny zostaje w widocznej części karty, gdy tabela przewija się w bok
const visibleWidthStyle = computed(() => (cardWidth.value ? { maxWidth: `${Math.max(cardWidth.value - 32, 320)}px` } : undefined))

function missingNames(stage, row) {
  const cell = row.cells[stage.index]
  return stage.points.filter((p, i) => cell.states[i] === 'skipped').map(p => p.name).join(', ')
}

function stageStatus(stage, row) {
  const cell = row.cells[stage.index]
  // po zawodach drużyna bez mety nie jest „teraz” na etapie — zostaje ostatnie podbicie
  if (cell.current) return phase.value === 'ended' ? 'last' : 'current'
  if (row.group === 'dnf' && row.currentIndex === stage.index) return 'withdrawn'
  if (cell.timeText) return 'time'
  if (cell.closed && cell.missing) return 'missing'
  if (cell.closed) return 'closed'
  return 'ahead'
}

const mapBase = computed(() => betaEventUrl(alias))
function mapUrl(row) {
  return mapBase.value ? `${mapBase.value}/bib/${encodeURIComponent(row.number)}` : ''
}
const splitsUrl = `/splits/${encodeURIComponent(alias)}`

// ---------- telefon / komputer ----------
const isPhone = ref(false)
let phoneMedia = null
function onMediaChange(e) { isPhone.value = e.matches }
function onKeydown(e) {
  if (e.key === 'Escape' && sheetOpen.value) sheetOpen.value = false
}

// ---------- pobieranie ----------
let refreshTimer = null
onMounted(async () => {
  document.documentElement.classList.add('rv-page')
  phoneMedia = window.matchMedia(PHONE_QUERY)
  isPhone.value = phoneMedia.matches
  phoneMedia.addEventListener?.('change', onMediaChange)
  document.addEventListener('keydown', onKeydown)

  // najpierw wydarzenie: start wydarzenia dla drużyn bez start_time i nazwa 1. etapu (marker startu)
  await resultsStore.fetchEventDetails(alias)
  await resultsStore.fetchResults(alias)
  loaded.value = true
  rebuild()
  refreshTimer = setInterval(() => {
    resultsStore.fetchEventDetails(alias)
    resultsStore.fetchResults(alias)
  }, REFRESH_MS)
})

onBeforeUnmount(() => {
  document.documentElement.classList.remove('rv-page')
  phoneMedia?.removeEventListener?.('change', onMediaChange)
  document.removeEventListener('keydown', onKeydown)
  clearInterval(refreshTimer)
  clearTimeout(changedTimer)
  cardObserver?.disconnect()
})

watch(() => resultsStore.results, () => {
  if (loaded.value) rebuild()
})

const updatedAtText = computed(() => (lastUpdate.value ? formatClock(lastUpdate.value) : ''))
// jak dawne /ar: wyniki z GPS / bez oficjalnej mety są poglądowe; link do pełnych wyników organizatora
const provisional = computed(() => rows.value.some(r => r.gpsOnly || r.group === 'course'))
const officialResultsUrl = computed(() => {
  const url = typeof event.value?.results_url === 'string' ? event.value.results_url.trim() : ''
  return /^https?:\/\/\S+$/i.test(url) ? url : ''
})
</script>

<template>
  <div class="rv ar">
    <EventHeader :event="event" :alias="alias" :last-update="lastUpdate" wide />

    <main class="ar__main">
      <!-- komputer: szukaj, płeć, kategoria -->
      <div v-if="!isPhone" class="ar-toolbar">
        <label class="ar-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <input v-model="query" type="search" enterkeyhint="search" placeholder="Szukaj: drużyna, zawodnik, nr" aria-label="Szukaj drużyny po nazwie, składzie albo numerze" />
        </label>
        <div v-if="hasGender" class="ar-seg" role="group" aria-label="Płeć">
          <button v-for="opt in [['all', 'Wszyscy'], ['K', 'Kobiety'], ['M', 'Mężczyźni']]" :key="opt[0]" type="button" :aria-pressed="gender === opt[0] ? 'true' : 'false'" @click="gender = opt[0]">{{ opt[1] }}</button>
        </div>
        <label v-if="catOptions.length > 1" class="ar-select" :class="{ 'is-active': category !== 'all' }">
          <span>Kategoria:</span>
          <select v-model="category" aria-label="Kategoria">
            <option value="all">wszystkie</option>
            <option v-for="c in catOptions" :key="c.id" :value="c.id">{{ c.name }} · {{ c.count }}</option>
          </select>
        </label>
      </div>

      <!-- telefon: szukaj + Filtry -->
      <div v-else class="ar-ptools">
        <div class="ar-ptools__row">
          <label class="ar-search ar-search--phone">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input v-model="query" type="search" enterkeyhint="search" placeholder="Szukaj: drużyna, zawodnik" aria-label="Szukaj drużyny po nazwie, składzie albo numerze" />
          </label>
          <button v-if="hasGender || catOptions.length > 1" type="button" class="ar-filters-btn" :class="{ 'is-active': activeFilterCount }" aria-haspopup="dialog" @click="sheetOpen = true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
            Filtry<span v-if="activeFilterCount" class="ar-filters-btn__badge">{{ activeFilterCount }}</span>
          </button>
        </div>
        <div v-if="activeFilterCount" class="ar-chips">
          <span v-if="gender !== 'all'" class="ar-chip">{{ gender === 'K' ? 'Kobiety' : 'Mężczyźni' }}<button type="button" :aria-label="`Usuń filtr ${gender === 'K' ? 'Kobiety' : 'Mężczyźni'}`" @click="gender = 'all'">×</button></span>
          <span v-if="category !== 'all'" class="ar-chip">{{ categoryLabel }}<button type="button" :aria-label="`Usuń filtr ${categoryLabel}`" @click="category = 'all'">×</button></span>
        </div>
      </div>

      <!-- legenda (komputer) -->
      <div v-if="!isPhone && loaded && rows.length" class="ar-legend" aria-label="Legenda">
        <span v-for="d in legendDisciplines" :key="d" class="ar-legend__item"><StageIcon :discipline="d" :size="20" />{{ DISCIPLINE_LABELS[d] }}</span>
        <span v-if="hasTask" class="ar-legend__item"><span class="pk pk--done pk--task" aria-hidden="true">★</span>zadanie specjalne</span>
        <span v-if="legendDisciplines.length || hasTask" class="ar-legend__sep" aria-hidden="true" />
        <span class="ar-legend__item"><span class="pk pk--done" :style="{ '--pk': stageViews[0]?.color }" aria-hidden="true">7</span>zaliczony</span>
        <span class="ar-legend__item"><span class="pk pk--skipped" aria-hidden="true">7</span>pominięty</span>
        <span class="ar-legend__item"><span class="pk pk--pending" aria-hidden="true">7</span>jeszcze przed drużyną</span>
        <span v-if="multiStage" class="ar-legend__item"><span class="ar-best">5:12:55</span>najszybszy etap</span>
      </div>

      <p v-if="loaded && rows.length" class="ar-order">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7.5h.01" /></svg>
        <span>{{ isPhone ? orderNoteShort : orderNote }}</span>
      </p>

      <p v-if="loaded && provisional && phase !== 'before'" class="ar-provisional">
        Wyniki poglądowe (na podstawie GPS)
        <a v-if="officialResultsUrl" :href="officialResultsUrl" target="_blank" rel="noopener" title="Link prowadzi do zewnętrznej strony">Pełne wyniki ↗</a>
      </p>

      <p v-if="filtersActive && loaded" class="ar-note">{{ filtered.length }} z {{ rows.length }} · miejsca ogólne</p>

      <div v-if="!loaded" class="ar-empty">Wczytywanie wyników…</div>
      <div v-else-if="!rows.length" class="ar-empty">Brak drużyn na liście.</div>
      <div v-else-if="!items.length" class="ar-empty">Nikt nie pasuje do wyszukiwania.</div>

      <!-- komputer: tabela (AR1, AR5) -->
      <div v-else-if="!isPhone" ref="cardRef" class="ar-card">
        <div ref="scrollRef" class="ar-scroll" :class="{ 'is-scrolled': scrolledX }" @scroll.passive="onTableScroll">
          <table class="ar-table" :class="{ 'ar-table--dense': denseTable }">
            <caption class="visually-hidden">Wyniki rajdu: {{ event?.name || alias }}</caption>
            <thead>
              <tr>
                <th scope="col" class="c-place">Miejsce</th>
                <th scope="col" class="c-team">Drużyna</th>
                <th v-if="catOptions.length" scope="col" class="c-cat">Kat.</th>
                <th v-if="hasPoints" scope="col" class="c-num">Punkty</th>
                <th scope="col" class="c-pk">PK</th>
                <template v-if="multiStage">
                  <th v-for="stage in stageViews" :key="stage.index" scope="col" class="c-stage">
                    <span class="ar-stage-head">
                      <StageIcon :discipline="stage.discipline" />
                      <span class="ar-stage-head__text">
                        <span class="ar-stage-head__title">{{ stage.title }} · {{ stage.points.length }} PK</span>
                        <span v-if="stage.label" class="ar-stage-head__label" :title="stage.label">{{ stage.label }}</span>
                      </span>
                    </span>
                  </th>
                </template>
                <th v-else scope="col" class="c-stage c-stage--single">Punkty kontrolne</th>
                <th scope="col" class="c-num">Czas</th>
                <th scope="col" class="c-num">Strata</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in items" :key="item.key">
                <tr v-if="item.section" class="ar-section">
                  <th :colspan="columnCount" scope="colgroup"><span class="ar-section__text">{{ item.section }}</span></th>
                </tr>
                <template v-else>
                  <tr class="ar-row" :class="{ 'is-changed': changedIds.has(item.row.id), 'is-open': expandedId === item.row.id }" @click="toggle(item.row)">
                    <td class="c-place">
                      <span v-if="item.row.place" class="ar-medal" :class="`ar-medal--${item.row.place}`" :aria-label="`${item.row.place}. miejsce`">{{ item.row.place }}</span>
                      <span v-else-if="item.row.group === 'dnf'" class="ar-tag ar-tag--dnf">DNF</span>
                      <span v-else-if="item.row.group === 'dns'" class="ar-tag">DNS</span>
                      <span v-else-if="item.row.outOfCompetition" class="ar-tag" title="Poza klasyfikacją">poza kl.</span>
                      <span v-else class="ar-dash">–</span>
                    </td>
                    <td class="c-team">
                      <button type="button" class="ar-team" :aria-expanded="expandedId === item.row.id ? 'true' : 'false'" @click.stop="toggle(item.row)">
                        <span class="ar-team__line"><span class="ar-bib">{{ item.row.number }}</span><span class="ar-team__name">{{ item.row.name }}</span></span>
                        <span v-if="item.row.lineup" class="ar-team__lineup">{{ item.row.lineup }}</span>
                      </button>
                    </td>
                    <td v-if="catOptions.length" class="c-cat">{{ item.row.categoryName }}<strong v-if="item.row.categoryPlace">{{ ' ' + item.row.categoryPlace + '.' }}</strong></td>
                    <td v-if="hasPoints" class="c-num"><strong v-if="item.row.points" class="ar-points">{{ item.row.points }}</strong><span v-else class="ar-dash">–</span></td>
                    <td class="c-pk"><strong>{{ item.row.punched }}</strong><span class="ar-of">/{{ item.row.totalPoints }}</span></td>

                    <td v-if="!stageViews.length" class="c-stage" />
                    <td v-for="stage in (multiStage ? stageViews : stageViews.slice(0, 1))" :key="stage.index" class="c-stage" :class="{ 'c-stage--single': !multiStage }">
                      <span class="ar-cell" :class="{ 'is-ahead': stageStatus(stage, item.row) === 'ahead' && item.row.group !== 'finished' }">
                        <span class="ar-pks" :style="pksStyle(stage)">
                          <span
                            v-for="(point, i) in stage.points"
                            :key="point.id"
                            class="pk"
                            :class="[`pk--${item.row.cells[stage.index].states[i]}`, { 'pk--task': point.task }]"
                            :style="{ '--pk': stage.color }"
                            :title="pkTitle(stage, point, item.row, i)"
                          >{{ point.task ? '★' : point.label }}</span>
                        </span>
                        <template v-if="multiStage">
                          <span v-if="stageStatus(stage, item.row) === 'current'" class="ar-cell__info">
                            <span class="ar-now"><span class="ar-now__dot" aria-hidden="true" />teraz <RunningTime v-if="phase === 'live'" :start-ms="item.row.cells[stage.index].startMs" short /></span>
                            <span v-if="item.row.lastName" class="ar-cell__last">{{ item.row.lastName }} {{ item.row.lastClock }}</span>
                          </span>
                          <span v-else-if="stageStatus(stage, item.row) === 'last'" class="ar-cell__info ar-cell__last">ostatni {{ item.row.lastName }} {{ item.row.lastClock }}</span>
                          <span v-else-if="stageStatus(stage, item.row) === 'withdrawn'" class="ar-cell__info ar-cell__withdrawn">{{ item.row.lastName ? `wycofani po ${item.row.lastName}` : 'wycofani' }}</span>
                          <span v-else-if="stageStatus(stage, item.row) === 'time'" class="ar-cell__info">
                            <span class="ar-cell__time" :class="{ 'ar-best': item.row.cells[stage.index].best }" :title="item.row.cells[stage.index].best ? 'Najszybszy etap' : undefined">{{ item.row.cells[stage.index].timeText }}</span>
                            <span v-if="item.row.cells[stage.index].place" class="ar-cell__place">({{ item.row.cells[stage.index].place }}.)</span>
                            <span v-else-if="item.row.cells[stage.index].missing" class="ar-cell__missing">· brak {{ item.row.cells[stage.index].missing }} PK</span>
                          </span>
                          <span v-else-if="stageStatus(stage, item.row) === 'missing'" class="ar-cell__info ar-cell__missing">brak {{ item.row.cells[stage.index].missing }} PK</span>
                        </template>
                        <span v-else-if="item.row.group === 'course' && item.row.lastName && phase !== 'before'" class="ar-cell__info ar-cell__last">ostatnio {{ item.row.lastName }} {{ item.row.lastClock }}</span>
                        <span v-else-if="item.row.group === 'dnf' && item.row.lastName" class="ar-cell__info ar-cell__withdrawn">wycofani po {{ item.row.lastName }}</span>
                      </span>
                    </td>

                    <td class="c-num c-time">
                      <template v-if="item.row.group === 'finished'">
                        <span class="ar-time">{{ item.row.timeText }}</span>
                        <span v-if="item.row.penaltyMin" class="ar-penalty" :title="item.row.penaltyComment ? `Kara: ${item.row.penaltyComment}` : undefined">{{ denseTable ? 'kara' : 'w tym kara' }} +{{ item.row.penaltyMin }} min</span>
                        <span v-if="item.row.gpsOnly" class="ar-gps" title="Czas wstępny z GPS">GPS</span>
                      </template>
                      <template v-else-if="item.row.group === 'course'">
                        <span v-if="phase === 'ended'" class="ar-muted">brak mety</span>
                        <template v-else-if="phase === 'live' && item.row.startMs != null && item.row.startMs <= (lastUpdate || 0)">
                          <RunningTime :start-ms="item.row.startMs" class="ar-time ar-time--running" />
                          <span class="ar-sub">w trasie</span>
                        </template>
                        <span v-else-if="item.row.startMs != null" class="ar-muted">start {{ formatClock(item.row.startMs).slice(0, 5) }}</span>
                      </template>
                      <span v-else-if="item.row.group === 'dnf'" class="ar-tag ar-tag--dnf">DNF</span>
                      <span v-else class="ar-tag">DNS</span>
                    </td>
                    <td class="c-num c-gap">{{ item.row.gapText }}</td>
                  </tr>

                  <!-- rozwinięta drużyna -->
                  <tr v-if="expandedId === item.row.id" class="ar-detail">
                    <td :colspan="columnCount">
                      <div class="ar-detail__inner" :style="visibleWidthStyle">
                      <div class="ar-detail__body">
                        <div v-if="multiStage" class="ar-detail__stages">
                          <div v-for="stage in stageViews" :key="stage.index" class="ar-dstage" :class="{ 'is-ahead': stageStatus(stage, item.row) === 'ahead' }">
                            <span class="ar-dstage__head"><StageIcon :discipline="stage.discipline" :size="20" />{{ stage.title }}<template v-if="stage.label"> · {{ stage.label }}</template></span>
                            <span class="ar-dstage__count">{{ item.row.cells[stage.index].doneCount }}/{{ stage.points.length }} PK</span>
                            <span v-if="stageStatus(stage, item.row) === 'current'" class="ar-dstage__line">
                              <span class="ar-now"><span class="ar-now__dot" aria-hidden="true" />teraz <RunningTime v-if="phase === 'live'" :start-ms="item.row.cells[stage.index].startMs" short /></span>
                              <span class="ar-dstage__where"><template v-if="item.row.cells[stage.index].startClock">od {{ item.row.cells[stage.index].startClock }}</template><template v-if="item.row.lastName"> · ostatni {{ item.row.lastName }} o {{ item.row.lastClock }}</template></span>
                            </span>
                            <span v-else-if="stageStatus(stage, item.row) === 'last'" class="ar-dstage__where">bez mety · ostatni {{ item.row.lastName }} o {{ item.row.lastClock }}</span>
                            <span v-else-if="stageStatus(stage, item.row) === 'withdrawn'" class="ar-dstage__line ar-cell__withdrawn">{{ item.row.lastName ? `wycofani po ${item.row.lastName}` : 'wycofani' }}</span>
                            <template v-else-if="item.row.cells[stage.index].closed">
                              <span class="ar-dstage__line">
                                <strong v-if="item.row.cells[stage.index].timeText">{{ item.row.cells[stage.index].timeText }}</strong>
                                <span v-if="item.row.cells[stage.index].place" class="ar-cell__place">({{ item.row.cells[stage.index].place }}.)</span>
                              </span>
                              <span v-if="item.row.cells[stage.index].missing" class="ar-dstage__missing">brak: {{ missingNames(stage, item.row) }}</span>
                              <span v-if="item.row.cells[stage.index].endClock" class="ar-dstage__where">{{ stage.endIsZone ? `strefa ${stage.endName}` : 'meta' }} · {{ item.row.cells[stage.index].endClock }}</span>
                            </template>
                            <span v-else class="ar-dstage__where">przed drużyną</span>
                          </div>
                        </div>
                        <div class="ar-detail__side">
                          <div v-if="item.row.members || item.row.bio" class="ar-lineup">
                            <strong>Skład</strong>
                            <template v-if="item.row.members"><span v-for="(m, i) in item.row.members" :key="i">{{ m }}</span></template>
                            <span v-else>{{ item.row.bio }}</span>
                          </div>
                          <p v-if="item.row.penaltyMin" class="ar-detail__note">Kara +{{ item.row.penaltyMin }} min<template v-if="item.row.penaltyComment">: {{ item.row.penaltyComment }}</template></p>
                          <p v-if="item.row.group === 'finished' && !hasPoints && AR_ORDER === 'checkpoints' && item.row.punched < item.row.totalPoints" class="ar-detail__note">{{ item.row.punched }} z {{ item.row.totalPoints }} PK – w klasyfikacji za drużynami z kompletem punktów.</p>
                        </div>
                      </div>
                      <div class="ar-detail__links">
                        <a v-if="mapUrl(item.row)" :href="mapUrl(item.row)" target="_blank" rel="noopener" class="ar-link">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" /><path d="M9 4v14M15 6v14" /></svg>
                          Pokaż na mapie
                        </a>
                        <a :href="splitsUrl" class="ar-link">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>
                          Pełna tabela czasów
                        </a>
                      </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
        <button v-if="groups.dns.length && !dnsVisible" type="button" class="ar-dns" @click="showDns = true">
          Pokaż {{ groups.dns.length }} {{ plural(groups.dns.length, 'drużynę, która nie wystartowała', 'drużyny, które nie wystartowały', 'drużyn, które nie wystartowały') }} (DNS)
        </button>
      </div>

      <!-- telefon: lista (AR2) z rozwinięciem (AR3) -->
      <ol v-else class="ar-plist" aria-label="Wyniki rajdu">
        <template v-for="item in items" :key="item.key">
          <li v-if="item.section" class="ar-plist__section" role="presentation"><h2>{{ item.section }}</h2></li>
          <li v-else class="ar-prow" :class="{ 'is-changed': changedIds.has(item.row.id), 'is-open': expandedId === item.row.id }">
            <button type="button" class="ar-prow__btn" :aria-expanded="expandedId === item.row.id ? 'true' : 'false'" @click="toggle(item.row)">
              <span class="ar-prow__place">
                <span v-if="item.row.place" class="ar-medal" :class="`ar-medal--${item.row.place}`" :aria-label="`${item.row.place}. miejsce`">{{ item.row.place }}</span>
                <span v-else-if="item.row.group === 'dnf'" class="ar-tag ar-tag--dnf">DNF</span>
                <span v-else-if="item.row.group === 'dns'" class="ar-tag">DNS</span>
                <span v-else class="ar-dash">–</span>
              </span>
              <span class="ar-prow__main">
                <span class="ar-prow__name">{{ item.row.name }}</span>
                <span class="ar-prow__sub">
                  {{ item.row.number }}<template v-if="item.row.categoryName"> · {{ item.row.categoryName }}<template v-if="item.row.categoryPlace">{{ ' ' + item.row.categoryPlace + '.' }}</template></template><template v-if="hasPoints && item.row.points"> · {{ item.row.points }} pkt</template> · <span class="ar-prow__pk">{{ item.row.punched }}/{{ item.row.totalPoints }} PK</span><template v-if="item.row.penaltyMin"> · <span class="ar-penalty ar-penalty--inline">kara +{{ item.row.penaltyMin }} min</span></template>
                </span>
                <span class="ar-bar" role="img" :aria-label="`Zaliczone ${item.row.punched} z ${item.row.totalPoints} PK`">
                  <span v-for="stage in stageViews" :key="stage.index" class="ar-bar__stage">
                    <span v-for="(point, i) in stage.points" :key="point.id" class="ar-bar__pk" :class="`ar-bar__pk--${item.row.cells[stage.index].states[i]}`" :style="{ '--pk': stage.color }" />
                  </span>
                </span>
              </span>
              <span class="ar-prow__right">
                <template v-if="item.row.group === 'finished'">
                  <span class="ar-time">{{ item.row.timeText }}</span>
                  <span class="ar-sub">{{ item.row.gapText || '\u00a0' }}</span>
                </template>
                <template v-else-if="item.row.group === 'course'">
                  <span v-if="phase === 'ended'" class="ar-muted">brak mety</span>
                  <template v-else-if="phase === 'live' && item.row.startMs != null && item.row.startMs <= (lastUpdate || 0)">
                    <RunningTime :start-ms="item.row.startMs" short class="ar-time ar-time--running" />
                    <span v-if="multiStage && item.row.currentIndex != null" class="ar-sub">Etap {{ item.row.currentIndex + 1 }} · teraz</span>
                    <span v-else class="ar-sub">w trasie</span>
                  </template>
                  <span v-else-if="item.row.startMs != null" class="ar-muted">start {{ formatClock(item.row.startMs).slice(0, 5) }}</span>
                </template>
              </span>
            </button>

            <div v-if="expandedId === item.row.id" class="ar-pdetail">
              <p v-if="item.row.lineup" class="ar-pdetail__lineup">{{ item.row.lineup }}</p>
              <div v-for="stage in (multiStage ? stageViews : stageViews.slice(0, 1))" :key="stage.index" class="ar-pstage" :class="{ 'is-ahead': stageStatus(stage, item.row) === 'ahead' && item.row.group !== 'finished' }">
                <span class="ar-pstage__head">
                  <StageIcon :discipline="stage.discipline" :size="22" />
                  <strong>{{ multiStage ? stage.title : 'Punkty kontrolne' }}<template v-if="multiStage && stage.label"> · {{ stage.label }}</template></strong>
                  <span class="ar-pstage__count">{{ item.row.cells[stage.index].doneCount }}/{{ stage.points.length }}</span>
                </span>
                <span class="ar-pks ar-pks--phone">
                  <span v-for="(point, i) in stage.points" :key="point.id" class="pk" :class="[`pk--${item.row.cells[stage.index].states[i]}`, { 'pk--task': point.task }]" :style="{ '--pk': stage.color }" :title="pkTitle(stage, point, item.row, i)">{{ point.task ? '★' : point.label }}</span>
                </span>
                <span v-if="multiStage" class="ar-pstage__line">
                  <template v-if="stageStatus(stage, item.row) === 'current'">
                    <span class="ar-now"><span class="ar-now__dot" aria-hidden="true" />teraz</span><template v-if="phase === 'live'"> · <RunningTime :start-ms="item.row.cells[stage.index].startMs" short /></template><template v-if="item.row.cells[stage.index].startClock"> · od {{ item.row.cells[stage.index].startClock }}</template><template v-if="item.row.lastName"> · ostatni {{ item.row.lastName }} o {{ item.row.lastClock }}</template>
                  </template>
                  <span v-else-if="stageStatus(stage, item.row) === 'last'">bez mety · ostatni {{ item.row.lastName }} o {{ item.row.lastClock }}</span>
                  <span v-else-if="stageStatus(stage, item.row) === 'withdrawn'" class="ar-cell__withdrawn">{{ item.row.lastName ? `wycofani po ${item.row.lastName}` : 'wycofani' }}</span>
                  <template v-else-if="item.row.cells[stage.index].closed">
                    <strong v-if="item.row.cells[stage.index].timeText" :class="{ 'ar-best': item.row.cells[stage.index].best }">{{ item.row.cells[stage.index].timeText }}</strong>
                    <span v-if="item.row.cells[stage.index].place" class="ar-cell__place"> ({{ item.row.cells[stage.index].place }}.)</span>
                    <span v-if="item.row.cells[stage.index].missing" class="ar-cell__missing"> brak {{ missingNames(stage, item.row) }}</span>
                    <template v-if="item.row.cells[stage.index].endClock"> · {{ stage.endIsZone ? stage.endName : 'meta' }} · {{ item.row.cells[stage.index].endClock }}</template>
                  </template>
                  <span v-else class="ar-muted">przed drużyną</span>
                </span>
              </div>
              <p v-if="item.row.penaltyMin" class="ar-detail__note">Kara +{{ item.row.penaltyMin }} min<template v-if="item.row.penaltyComment">: {{ item.row.penaltyComment }}</template></p>
              <div class="ar-detail__links">
                <a v-if="mapUrl(item.row)" :href="mapUrl(item.row)" target="_blank" rel="noopener" class="ar-link">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" /><path d="M9 4v14M15 6v14" /></svg>
                  Na mapie
                </a>
                <a :href="splitsUrl" class="ar-link">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>
                  Pełna tabela
                </a>
              </div>
            </div>
          </li>
        </template>
        <li v-if="groups.dns.length && !dnsVisible" role="presentation">
          <button type="button" class="ar-dns" @click="showDns = true">
            Pokaż {{ groups.dns.length }} {{ plural(groups.dns.length, 'drużynę, która nie wystartowała', 'drużyny, które nie wystartowały', 'drużyn, które nie wystartowały') }}
          </button>
        </li>
      </ol>
    </main>

    <PartnersFooter :sponsors="sponsors" :updated-at="phase === 'live' ? updatedAtText : ''" wide />

    <!-- telefon: arkusz „Filtry” -->
    <div v-if="sheetOpen" class="ar-sheet" @click.self="sheetOpen = false">
      <div class="ar-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="ar-sheet-title">
        <div class="ar-sheet__head">
          <h2 id="ar-sheet-title">Filtry</h2>
          <button type="button" class="ar-linkbtn" @click="clearFilters">Wyczyść</button>
        </div>
        <template v-if="hasGender">
          <h3>Płeć</h3>
          <div class="ar-seg ar-seg--wide" role="group" aria-label="Płeć">
            <button v-for="opt in [['all', 'Wszyscy'], ['K', 'Kobiety'], ['M', 'Mężczyźni']]" :key="opt[0]" type="button" :aria-pressed="gender === opt[0] ? 'true' : 'false'" @click="gender = opt[0]">{{ opt[1] }}</button>
          </div>
        </template>
        <template v-if="catOptions.length > 1">
          <h3>Kategoria</h3>
          <div class="ar-sheet__chips">
            <button type="button" class="ar-pick" :aria-pressed="category === 'all' ? 'true' : 'false'" @click="category = 'all'">Wszystkie</button>
            <button v-for="c in catOptions" :key="c.id" type="button" class="ar-pick" :aria-pressed="category === c.id ? 'true' : 'false'" @click="category = c.id">{{ c.name }} · {{ c.count }}</button>
          </div>
        </template>
        <button type="button" class="ar-sheet__go" @click="sheetOpen = false">Pokaż {{ filtered.length }} {{ plural(filtered.length, 'drużynę', 'drużyny', 'drużyn') }}</button>
      </div>
    </div>
  </div>
</template>

<style>
/* App.vue ustawia #app na wyśrodkowany tekst z marginesem — ta strona ma własny układ (jak /{alias}) */
html.rv-page #app {
  margin-top: 0;
  text-align: left;
  color: var(--sledz-text);
}
html.rv-page body {
  background: var(--sledz-surface-alt);
}
</style>

<style scoped>
/* Kolory tylko z palety --sledz-*; jaśniejsze odcienie przez color-mix z zapasem z palety przed nim
   (starsze przeglądarki) — zasada z recenzji PR #129. */
.ar {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--sledz-surface-alt);
  color: var(--sledz-text);
}
.ar__main {
  flex: 1;
  width: 100%;
  max-width: 1376px;
  margin: 0 auto;
  padding: 18px 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ---------- pasek ---------- */
.ar-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.ar-search {
  flex: none;
  width: 320px;
  height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid var(--sledz-border);
  border-radius: 10px;
  background: var(--sledz-surface);
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--sledz-text-muted);
}
.ar-search:focus-within {
  border-color: var(--sledz-primary);
  box-shadow: 0 0 0 3px var(--sledz-primary-soft);
}
.ar-search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: var(--sledz-text);
}
.ar-seg {
  display: inline-flex;
  padding: 3px;
  border-radius: 20px;
  background: var(--sledz-border-soft);
}
.ar-seg button {
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 17px;
  background: transparent;
  color: var(--sledz-text-secondary);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.ar-seg button[aria-pressed='true'] {
  background: var(--sledz-surface);
  color: var(--sledz-text);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
.ar-seg--wide {
  display: flex;
}
.ar-seg--wide button {
  flex: 1;
  height: 44px;
  border-radius: 22px;
}
.ar-select {
  height: 40px;
  margin: 0;
  padding: 0 4px 0 12px;
  border: 1px solid var(--sledz-border);
  border-radius: 10px;
  background: var(--sledz-surface);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--sledz-text-muted);
}
.ar-select.is-active {
  border: 1.5px solid var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.ar-select select {
  height: 36px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  color: var(--sledz-text);
  cursor: pointer;
}

/* ---------- legenda i opis kolejności ---------- */
.ar-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 13px;
  color: var(--sledz-text-secondary);
}
.ar-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}
.ar-legend__sep {
  width: 1px;
  height: 18px;
  background: var(--sledz-border);
}
.ar-order {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: var(--sledz-text-muted);
}
.ar-order svg {
  flex: none;
}
.ar-provisional {
  margin: 0;
  font-size: 13px;
  color: var(--sledz-text-muted);
}
.ar-provisional a {
  margin-left: 6px;
  color: var(--sledz-primary-strong);
  font-weight: 700;
  white-space: nowrap;
}
.ar-note {
  margin: 0;
  font-size: 13.5px;
  color: var(--sledz-text-muted);
}
.ar-empty {
  padding: 40px 16px;
  text-align: center;
  color: var(--sledz-text-muted);
}

/* ---------- kwadrat PK ---------- */
.pk {
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  cursor: default;
}
.pk--done {
  background: var(--pk, var(--sledz-stage-other));
  color: var(--sledz-surface);
}
.pk--done.pk--task {
  background: var(--sledz-brand);
  color: var(--sledz-brand-ink);
  font-size: 12px;
}
.pk--skipped {
  background: var(--sledz-warning-soft);
  color: var(--sledz-warning-ink);
  box-shadow: inset 0 0 0 1.5px var(--sledz-warning);
}
.pk--pending {
  background: var(--sledz-surface-alt);
  color: var(--sledz-text-muted);
  box-shadow: inset 0 0 0 1px var(--sledz-border-soft);
}
.pk--task {
  font-size: 11.5px;
}
.ar-pks {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}
.ar-pks--phone {
  max-width: none;
  gap: 4px;
}
.ar-pks--phone .pk {
  width: 22px;
  height: 22px;
  font-size: 10.5px;
}
.ar-best {
  padding: 0 5px;
  border-radius: 5px;
  background: var(--sledz-brand);
  color: var(--sledz-brand-ink);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

/* ---------- tabela (komputer) ---------- */
.ar-card {
  background: var(--sledz-surface);
  border: 1px solid var(--sledz-border-soft);
  border-radius: 14px;
  overflow: hidden;
}
/* Tylko w bok: samo overflow-x: auto robi z overflow-y też auto, a ułamek piksela wysokości tabeli
   dawał drugi pionowy pasek przewijania w karcie (zgłoszenie 04.11.2026, rajd z 7 etapami). */
.ar-scroll {
  overflow-x: auto;
  overflow-y: hidden;
}
.ar-table {
  width: 100%;
  border-collapse: collapse;
}
.ar-table thead th {
  height: 44px;
  padding: 8px 8px;
  background: var(--sledz-surface-alt);
  border-bottom: 1px solid var(--sledz-border-soft);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
  text-align: left;
  white-space: nowrap;
  vertical-align: bottom;
}
.ar-table thead th.c-stage {
  height: 60px;
  text-transform: none;
  letter-spacing: 0;
  white-space: normal;
}
.ar-table td {
  padding: 9px 8px;
  border-bottom: 1px solid var(--sledz-border-soft);
  vertical-align: middle;
  font-size: 14px;
}
.ar-row {
  cursor: pointer;
}
.ar-row:hover td {
  background: var(--sledz-surface-alt);
}
.ar-row.is-open td {
  background: var(--sledz-primary-soft);
  background: color-mix(in srgb, var(--sledz-primary-soft) 60%, var(--sledz-surface));
  border-bottom-color: transparent;
}
.ar-row.is-changed td {
  animation: ar-flash 1.2s ease-out;
  background: var(--sledz-highlight);
}
@keyframes ar-flash {
  0% { background: var(--sledz-highlight-flash); }
  100% { background: var(--sledz-highlight); }
}
.c-place {
  width: 76px;
  min-width: 76px; /* = left kolumny Drużyna przy przyklejeniu — bez szpary przy przewijaniu w bok */
  text-align: center;
}
.c-team {
  min-width: 180px;
}
/* przyklejone przy przewijaniu w bok (tylko gdy tabela nie mieści się w karcie) */
.ar-table .c-place,
.ar-table .c-team {
  position: sticky;
  z-index: 1;
  background: var(--sledz-surface);
}
.ar-table .c-place {
  left: 0;
}
.ar-table .c-team {
  left: 76px;
}
.ar-table thead th.c-place,
.ar-table thead th.c-team {
  z-index: 2;
  background: var(--sledz-surface-alt);
}
.ar-scroll.is-scrolled .c-team {
  box-shadow: inset -1px 0 0 var(--sledz-border-soft), 6px 0 8px -6px rgba(15, 23, 42, 0.25);
}
.c-cat {
  width: 90px;
  white-space: nowrap;
  color: var(--sledz-text-secondary);
}
.c-cat strong {
  color: var(--sledz-text);
}
.c-pk {
  width: 64px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.c-pk strong {
  font-size: 14.5px;
  font-weight: 800;
}
.ar-of {
  font-weight: 600;
  color: var(--sledz-text-muted);
}
.c-num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.ar-table thead th.c-num {
  text-align: right;
}
.c-time {
  width: 112px;
}
.c-gap {
  width: 96px;
  color: var(--sledz-text-muted);
}
/* szerokość etapu wyznaczają kwadraty (pksStyle), nie stała minimalna — przy 7 etapach 150 px na etap
   rozpychało tabelę poza kartę */
.c-stage--single {
  min-width: 150px;
}
.ar-stage-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ar-stage-head__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.ar-stage-head__title {
  font-size: 13px;
  font-weight: 800;
  color: var(--sledz-text);
  white-space: nowrap; /* „Etap 4 · 4 PK” w jednej linii */
}
.ar-stage-head__label {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--sledz-text-muted);
}
.ar-section__text {
  position: sticky;
  left: 16px;
}
.ar-section th {
  padding: 18px 16px 8px;
  border-bottom: 1px solid var(--sledz-border-soft);
  background: var(--sledz-surface);
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
  text-align: left;
}
.ar-team {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  max-width: 220px;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.ar-team:focus-visible {
  outline: 2px solid var(--sledz-primary);
  outline-offset: 3px;
  border-radius: 4px;
}
.ar-team__line {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.ar-team__name {
  font-size: 15px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ar-team__lineup {
  max-width: 210px;
  font-size: 12.5px;
  color: var(--sledz-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ar-bib {
  flex: none;
  min-width: 38px;
  padding: 2px 6px;
  border-radius: 6px;
  background: var(--sledz-border-soft);
  color: var(--sledz-text-secondary);
  text-align: center;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.ar-cell {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.ar-cell.is-ahead {
  opacity: 0.85;
}
.ar-cell__info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  max-width: 207px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.ar-cell__info > span {
  white-space: nowrap;
}
.ar-cell__time {
  font-weight: 700;
  color: var(--sledz-text);
}
.ar-cell__place {
  color: var(--sledz-text-muted);
}
.ar-cell__missing {
  color: var(--sledz-warning-ink);
  font-weight: 700;
}
.ar-cell__last {
  font-size: 12px;
  color: var(--sledz-text-secondary);
}
.ar-cell__withdrawn {
  color: var(--sledz-live-strong);
  font-weight: 700;
}
.ar-now {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 1px 8px;
  border-radius: 8px;
  border: 1px dashed var(--sledz-primary);
  color: var(--sledz-primary-deep);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}
.ar-table--dense .ar-pks {
  gap: 2px;
}
.ar-table--dense .ar-pks .pk {
  width: 16px;
  height: 16px;
  font-size: 9px;
}
.ar-table--dense .ar-pks .pk--task {
  font-size: 10.5px;
}
.ar-table--dense .ar-stage-head {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.ar-table--dense .ar-stage-head__title {
  font-size: 12px;
  white-space: normal;
}
.ar-table--dense .ar-stage-head__label {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 11px;
}
.ar-table--dense .ar-now {
  gap: 4px;
  padding: 1px 5px;
  font-size: 11.5px;
}
.ar-table--dense .ar-team {
  max-width: 190px;
}
.ar-table--dense .ar-team__lineup {
  max-width: 180px;
}
.ar-table--dense .ar-cell__info {
  font-size: 12.5px;
}
.ar-now__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--sledz-live);
}
.ar-time {
  display: block;
  font-size: 16px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--sledz-text);
}
.ar-time--running {
  font-size: 15px;
  font-weight: 400;
  font-style: italic;
  color: var(--sledz-text-secondary);
}
.ar-sub {
  display: block;
  font-size: 12.5px;
  color: var(--sledz-text-muted);
  white-space: nowrap;
}
.ar-muted {
  color: var(--sledz-text-muted);
  font-size: 13.5px;
}
.ar-penalty {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--sledz-warning-ink);
  white-space: nowrap;
}
.ar-penalty--inline {
  display: inline;
}
.ar-gps {
  display: inline-block;
  margin-top: 2px;
  padding: 0 5px;
  border-radius: 4px;
  background: var(--sledz-warning-soft);
  color: var(--sledz-warning-ink);
  font-size: 10.5px;
  font-weight: 800;
}
.ar-points {
  font-size: 15px;
  font-weight: 800;
}
.ar-medal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 4px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--sledz-text);
}
.ar-medal--1,
.ar-medal--2,
.ar-medal--3 {
  font-size: 14px;
  font-weight: 900;
  color: var(--sledz-brand-ink);
  box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.12);
}
.ar-medal--1 { background: var(--sledz-brand); }
.ar-medal--2 { background: var(--sledz-medal-silver); }
.ar-medal--3 { background: var(--sledz-medal-bronze); }
.ar-tag {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--sledz-border-soft);
  color: var(--sledz-text-secondary);
  font-size: 11.5px;
  font-weight: 800;
  white-space: nowrap;
}
.ar-tag--dnf {
  background: var(--sledz-surface-alt);
  background: color-mix(in srgb, var(--sledz-live) 14%, var(--sledz-surface));
  color: var(--sledz-live-strong);
}
.ar-dash {
  color: var(--sledz-text-muted);
}
.ar-dns {
  display: block;
  width: 100%;
  padding: 14px;
  border: none;
  border-top: 1px solid var(--sledz-border-soft);
  background: var(--sledz-surface);
  color: var(--sledz-primary-strong);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

/* ---------- rozwinięcie ---------- */
.ar-detail td {
  padding: 4px 16px 16px;
  background: var(--sledz-primary-soft);
  background: color-mix(in srgb, var(--sledz-primary-soft) 60%, var(--sledz-surface));
  cursor: default;
}
.ar-detail__inner {
  position: sticky;
  left: 16px;
}
.ar-detail__body {
  display: flex;
  gap: 16px;
  align-items: stretch;
}
.ar-detail__stages {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
}
.ar-dstage {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid var(--sledz-border-soft);
  border-radius: 10px;
  background: var(--sledz-surface);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.ar-dstage.is-ahead {
  color: var(--sledz-text-muted);
}
.ar-dstage__head {
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 800;
  color: var(--sledz-text);
}
.ar-dstage__count {
  color: var(--sledz-text-muted);
}
.ar-dstage__line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}
.ar-dstage__line strong {
  font-size: 15px;
}
.ar-dstage__missing {
  color: var(--sledz-warning-ink);
  font-weight: 700;
}
.ar-dstage__where {
  color: var(--sledz-text-muted);
}
.ar-detail__side {
  flex: none;
  width: 260px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ar-lineup {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid var(--sledz-border-soft);
  border-radius: 10px;
  background: var(--sledz-surface);
  font-size: 13.5px;
}
.ar-lineup strong {
  margin-bottom: 2px;
  font-size: 12px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
}
.ar-detail__note {
  margin: 0;
  font-size: 13px;
  color: var(--sledz-text-secondary);
}
.ar-detail__links {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.ar-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 18px;
  border: 1.5px solid var(--sledz-primary);
  background: var(--sledz-surface);
  color: var(--sledz-primary-deep);
  font-size: 13.5px;
  font-weight: 800;
  text-decoration: none;
}
.ar-link:hover {
  background: var(--sledz-primary-soft);
}

/* ---------- telefon ---------- */
.ar-ptools {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ar-ptools__row {
  display: flex;
  gap: 8px;
}
.ar-search--phone {
  flex: 1;
  width: auto;
  height: 44px;
}
.ar-filters-btn {
  flex: none;
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--sledz-border);
  border-radius: 10px;
  background: var(--sledz-surface);
  color: var(--sledz-text);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
}
.ar-filters-btn.is-active {
  border-color: var(--sledz-primary);
  color: var(--sledz-primary-deep);
}
.ar-filters-btn__badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--sledz-primary);
  color: var(--sledz-surface);
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ar-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ar-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 4px 0 12px;
  border-radius: 16px;
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
  font-size: 13.5px;
  font-weight: 700;
}
.ar-chip button {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 14px;
  background: transparent;
  color: inherit;
  font-size: 18px;
  line-height: 1;
}
.ar-plist {
  margin: 0;
  padding: 0;
  list-style: none;
  background: var(--sledz-surface);
  border: 1px solid var(--sledz-border-soft);
  border-radius: 14px;
  overflow: hidden;
}
.ar-plist__section h2 {
  margin: 0;
  padding: 16px 14px 6px;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
}
.ar-prow {
  border-bottom: 1px solid var(--sledz-border-soft);
}
.ar-prow.is-changed {
  animation: ar-flash 1.2s ease-out;
  background: var(--sledz-highlight);
}
.ar-prow.is-open {
  background: var(--sledz-primary-soft);
  background: color-mix(in srgb, var(--sledz-primary-soft) 60%, var(--sledz-surface));
}
.ar-prow__btn {
  width: 100%;
  min-height: 64px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  color: inherit;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
}
.ar-prow__place {
  flex: none;
  width: 34px;
  padding-top: 1px;
  display: flex;
  justify-content: center;
}
.ar-prow__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.ar-prow__name {
  font-size: 15.5px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ar-prow__sub {
  font-size: 12.5px;
  color: var(--sledz-text-muted);
  font-variant-numeric: tabular-nums;
}
.ar-prow__pk {
  font-weight: 700;
  color: var(--sledz-text-secondary);
}
.ar-prow__right {
  flex: none;
  min-width: 76px;
  text-align: right;
}
.ar-prow__right .ar-time {
  font-size: 15px;
}
.ar-bar {
  margin-top: 3px;
  display: flex;
  gap: 4px;
}
.ar-bar__stage {
  flex: 1 1 auto;
  display: flex;
  gap: 1px;
}
.ar-bar__pk {
  flex: 1 1 0;
  height: 6px;
  min-width: 2px;
  border-radius: 1px;
  background: var(--sledz-border-soft);
}
.ar-bar__pk--done {
  background: var(--pk, var(--sledz-stage-other));
}
.ar-bar__pk--skipped {
  background: var(--sledz-warning);
}
.ar-pdetail {
  padding: 0 12px 14px 56px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ar-pdetail__lineup {
  margin: 0;
  font-size: 13px;
  color: var(--sledz-text-secondary);
}
.ar-pstage {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.ar-pstage.is-ahead {
  color: var(--sledz-text-muted);
}
.ar-pstage__head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ar-pstage__count {
  margin-left: auto;
  color: var(--sledz-text-muted);
}
.ar-pstage__line {
  color: var(--sledz-text-secondary);
}
.ar-sheet {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: flex-end;
}
.ar-sheet__panel {
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 18px 16px 20px;
  border-radius: 18px 18px 0 0;
  background: var(--sledz-surface);
}
.ar-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ar-sheet__head h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
}
.ar-sheet h3 {
  margin: 16px 0 8px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
}
.ar-sheet__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ar-pick {
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid var(--sledz-border);
  border-radius: 20px;
  background: var(--sledz-surface);
  color: var(--sledz-text);
  font-size: 14px;
  font-weight: 700;
}
.ar-pick[aria-pressed='true'] {
  border-color: var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.ar-sheet__go {
  width: 100%;
  height: 48px;
  margin-top: 20px;
  border: none;
  border-radius: 12px;
  background: var(--sledz-primary-strong);
  color: var(--sledz-surface);
  font-size: 15px;
  font-weight: 800;
}
.ar-linkbtn {
  border: none;
  background: transparent;
  color: var(--sledz-primary-strong);
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 767.98px) {
  .ar__main {
    padding: 12px 12px 20px;
    gap: 10px;
  }
  .ar-order {
    font-size: 12.5px;
  }
}
</style>
